import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Divider,
  Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
// Note: Don't import React Router Link here as ErrorBoundary can be outside Router context

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

/**
 * Error Boundary component for handling JavaScript errors in React components
 * Provides graceful error handling and user feedback for production applications
 * 
 * Features:
 * - Catches and displays JavaScript errors in the component tree
 * - Provides user-friendly error messages and recovery options
 * - Logs errors for debugging and monitoring
 * - Generates unique error IDs for tracking
 * - Offers multiple recovery paths (refresh, home, contact)
 */
class ErrorBoundary extends Component<Props, State> {
  private retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Send error to monitoring service
    this.logErrorToService(error, errorInfo);

    // Announce error to screen readers
    this.announceErrorToScreenReader();
  }

  componentWillUnmount(): void {
    // Clear any pending timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo): void => {
    // Log to external service (e.g., Sentry, LogRocket)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.message,
        fatal: false,
        error_id: this.state.errorId,
        component_stack: errorInfo.componentStack,
      });
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.table({
        'Error ID': this.state.errorId,
        'Error Message': error.message,
        'Error Stack': error.stack,
        'Timestamp': new Date().toISOString(),
      });
    }
  };

  private announceErrorToScreenReader = (): void => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'assertive');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = 'An error has occurred. Please try refreshing the page or contact support if the problem persists.';
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 3000);
  };

  private handleRetry = (): void => {
    // Clear the error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    });

    // Announce retry to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Retrying to load the page.';
    document.body.appendChild(announcement);
    
    const timeout = setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 2000);
    
    this.retryTimeouts.push(timeout);
  };

  private handleRefresh = (): void => {
    window.location.reload();
  };

  private copyErrorDetails = (): void => {
    const { error, errorInfo, errorId } = this.state;
    const errorDetails = {
      errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: {
        message: error?.message,
        stack: error?.stack,
      },
      componentStack: errorInfo?.componentStack,
    };

    const errorText = JSON.stringify(errorDetails, null, 2);
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(errorText).then(() => {
        // Show success feedback
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = 'Error details copied to clipboard.';
        document.body.appendChild(announcement);
        
        setTimeout(() => {
          if (document.body.contains(announcement)) {
            document.body.removeChild(announcement);
          }
        }, 2000);
      });
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            p: 2,
          }}
          data-testid="error-boundary"
        >
          <Container maxWidth="md">
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 3,
              }}
            >
              {/* Error Icon */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.light',
                  mb: 3,
                }}
              >
                <BugReportIcon sx={{ fontSize: '2.5rem', color: 'error.main' }} />
              </Box>

              {/* Error Title */}
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                data-testid="error-title"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                Oops! Something went wrong
              </Typography>

              {/* Error Description */}
              <Typography
                variant="body1"
                color="text.secondary"
                data-testid="error-description"
                sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
              >
                We're sorry, but an unexpected error has occurred. Our team has been notified 
                and we're working to fix this issue. Please try one of the options below.
              </Typography>

              {/* Error ID for Support */}
              {this.state.errorId && (
                <Alert
                  severity="info"
                  data-testid="error-id-alert"
                  sx={{ mb: 4, textAlign: 'left' }}
                >
                  <AlertTitle>Error ID</AlertTitle>
                  {this.state.errorId}
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Please include this ID when contacting support.
                  </Typography>
                </Alert>
              )}

              {/* Recovery Actions */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  data-testid="retry-button"
                  sx={{ minWidth: 140 }}
                >
                  Try Again
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                  data-testid="refresh-button"
                  sx={{ minWidth: 140 }}
                >
                  Refresh Page
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  component="a"
                  href="/"
                  startIcon={<HomeIcon />}
                  data-testid="home-button"
                  sx={{ minWidth: 140 }}
                >
                  Go Home
                </Button>
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Additional Help */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Need help? Contact our support team:
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  component="a"
                  href="mailto:support@vegadigital.com"
                  startIcon={<EmailIcon />}
                  data-testid="email-support-button"
                  sx={{ color: 'text.secondary' }}
                >
                  support@vegadigital.com
                </Button>

                <Button
                  component="a"
                  href="tel:+15551234567"
                  startIcon={<PhoneIcon />}
                  data-testid="phone-support-button"
                  sx={{ color: 'text.secondary' }}
                >
                  +1 (555) 123-4567
                </Button>
              </Stack>

              {/* Developer Tools */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ mt: 4, textAlign: 'left' }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Developer Information
                  </Typography>
                  
                  <Button
                    onClick={this.copyErrorDetails}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                  >
                    Copy Error Details
                  </Button>

                  <Alert severity="error" sx={{ textAlign: 'left' }}>
                    <AlertTitle>Error Details</AlertTitle>
                    <Typography
                      component="pre"
                      sx={{
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        overflow: 'auto',
                        maxHeight: 200,
                      }}
                    >
                      {this.state.error.message}
                      {'\n\n'}
                      {this.state.error.stack}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;